import {type Line} from '../../../util/plotHelpers/types';
import {type UPlotMode} from '../../../util/rampFeatureFlags/linePlotDynamicConfigs';
import {getProfiledAlignedData} from './getAlignedData';
import {type FacetData, getFacetedData} from './getFacetedData';
import {getSeriesEntries} from './getSeriesEntries';
import {getUPlotTelemetryContext} from './uPlotTelemetry';

/**
 * Prepare the chart's lines and min/max bands once so the main and highlighted
 * layers can draw the same data. Aligned mode gives all lines a shared set of
 * X values; faceted mode keeps each line's own X values.
 */
export function prepareUPlotData(
  lines: Line[],
  xLogScale?: boolean,
  uPlotMode: UPlotMode = 'aligned'
) {
  const entries = getSeriesEntries(lines);
  // An empty chart uses aligned mode because uPlot's faceted mode needs a line to draw.
  if (uPlotMode === 'faceted' && entries.seriesEntries.length > 1) {
    return {
      ...entries,
      uPlotMode,
      data: getFacetedData(entries, xLogScale),
      alignmentMeasurement: undefined,
    };
  }
  return {
    uPlotMode: 'aligned' as const,
    ...entries,
    ...getProfiledAlignedData(entries.seriesEntries, xLogScale),
  };
}

export type PreparedUPlotData = ReturnType<typeof prepareUPlotData>;

const PREPARATION_SAMPLE_RATE = 0.15;

export type UPlotPreparationMeasurement = {
  startTime: number;
  duration: number;
  context: ReturnType<typeof getUPlotTelemetryContext> & {
    uPlotMode: UPlotMode;
    xLogScale: boolean;
    sampleRate: number;
    samplingUnit: 'committed-panel-preparation';
  };
};

/** Time the owning panel's data preparation; drawing layers only read its result. */
export function getProfiledUPlotData(
  lines: Line[],
  xLogScale?: boolean,
  uPlotMode: UPlotMode = 'aligned'
): {
  preparedData: PreparedUPlotData;
  measurement: UPlotPreparationMeasurement | undefined;
} {
  const sampled = Math.random() < PREPARATION_SAMPLE_RATE;
  const startMark = sampled ? performance.now() : 0;
  const preparedData = prepareUPlotData(lines, xLogScale, uPlotMode);
  const duration = sampled ? performance.now() - startMark : 0;
  if (!sampled) {
    return {preparedData, measurement: undefined};
  }
  const hasPoints =
    preparedData.uPlotMode === 'aligned'
      ? preparedData.data[0].length > 0
      : preparedData.data.some(
          (facet, index) => index > 0 && (facet as FacetData)[0].length > 0
        );
  if (!hasPoints) {
    return {preparedData, measurement: undefined};
  }
  let context: UPlotPreparationMeasurement['context'];
  try {
    context = {
      ...getUPlotTelemetryContext(lines, preparedData.seriesEntries),
      uPlotMode: preparedData.uPlotMode,
      xLogScale: xLogScale === true,
      sampleRate: PREPARATION_SAMPLE_RATE,
      samplingUnit: 'committed-panel-preparation',
    };
  } catch {
    // Optional telemetry must not interrupt a successfully prepared chart.
    return {preparedData, measurement: undefined};
  }
  return {
    preparedData,
    measurement: {
      startTime: performance.timeOrigin + startMark,
      duration,
      context,
    },
  };
}
